import React, { Suspense } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import LuxonUtils from '@date-io/luxon';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import { ModalProvider } from './contexts/modal-context';
import { AuthProvider } from '@/contexts/auth-context';
import { EventProvider } from './contexts/event-context';

import client from './apollo-client';
import theme from './theme';
import DevLang from './utils/dev-lang';
import './utils/i18n-config';
import { SnackbarProvider } from './contexts/snackbar-context';
import initDatadogRum from './utils/statistics/datadog-rum-init';
import initMarketingTrackers from './utils/statistics/init-marketing-trackers';
import Loading from './components/loading';
import ErrorBoundary from './components/error-boundary';
import GlobalErrorHandler from './components/global-error-handler';
import { router } from './routes';
import GrowthbookAppProvider from './experiments/growthbook-provider';
import './app.scss';

initDatadogRum();
initMarketingTrackers();

const App: React.FC = () => (
  <GlobalErrorHandler>
    <ApolloProvider client={client}>
      <EventProvider>
        <MuiThemeProvider theme={theme}>
          <AuthProvider>
            <GrowthbookAppProvider>
              <MuiPickersUtilsProvider utils={LuxonUtils}>
                <ModalProvider>
                  <Suspense fallback={<Loading />}>
                    <SnackbarProvider>
                      <ErrorBoundary>
                        <DevLang>
                          <RouterProvider router={router} future={{ v7_startTransition: true }} />
                        </DevLang>
                      </ErrorBoundary>
                    </SnackbarProvider>
                  </Suspense>
                </ModalProvider>
              </MuiPickersUtilsProvider>
            </GrowthbookAppProvider>
          </AuthProvider>
        </MuiThemeProvider>
      </EventProvider>
    </ApolloProvider>
  </GlobalErrorHandler>
);

export default App;
